import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { treatExperiment } from '#src/app/shared/api/experiments';
import { isMobile } from '#src/app/shared/utils/userAgent';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['petitionDetailsShareCtaPlacementRerunExperiment'];

type Params = {
	experiment: Experiment;
	utilityContext: UtilityContext;
};

/**
 * `MobileSignButtonContainer` passes the returned, `treated`-corrected experiment straight into
 * `useTrackExperimentExposure` — narrowing this to a bare variation string would silently break
 * that.
 */
export async function normalizePetitionDetailsShareCtaPlacementRerunExperiment({
	experiment,
	utilityContext,
}: Params): Promise<Experiment> {
	if (!shouldRunExperiment({ experiment, utilityContext })) {
		// Experiment Service bucketing is keyed on UUID alone, so `experiment.variation`/`treated` can
		// already reflect a treatment arm here even though this user shouldn't see one (non-mobile or FC
		// disabled) — e.g. a user treated on a prior mobile visit revisiting on desktop. Force `control`
		// so the rendered UI respects the gate, and force `treated: false` so `useTrackExperimentExposure`
		// doesn't fire a control `$exposure` for this out-of-scope visit.
		return { ...experiment, variation: 'control', treated: false };
	}

	if (experiment.override) {
		// A QA/dev `?exo.` override already forced `variation`/`override: true` upstream, and
		// `useTrackExperimentExposure` already no-ops `$exposure` for `override`. Pass it through
		// unchanged instead of calling `treatExperiment`: that hits the real Experiment Service treat
		// endpoint for the session UUID, which would register a real bucket assignment for a visit
		// that was never a genuine assignment — polluting `experiment_variations` for that user.
		return experiment;
	}

	// `treated` reflects whether the server actually registered the treatment — a bot session or a
	// not-yet-treatable assignment can resolve without error while still returning `false`, and
	// unconditionally reporting `true` here would let `useTrackExperimentExposure` fire a bogus
	// `$exposure` for a visit the server never treated.
	const treated = await treatExperiment(utilityContext, experiment.experimentName);

	return { ...experiment, treated };
}

/**
 * Uses `isMobile()` (matches both `mobile` and `tablet` device types), same as the sibling
 * normalizers. This is the clean CHANGE-98647 re-run of the share CTA placement experiment, and
 * it reuses the same mobile-only, FC-gated scope as its predecessor and as
 * `petitionDetailsShareCtaCopyExperiment` — without this server-side gate, a desktop visitor would
 * still get a real Experiment Service assignment even though they can never see the affordance.
 */
function shouldRunExperiment({ experiment, utilityContext }: Params): boolean {
	return isEnabledByFC(experiment) && isMobile(utilityContext);
}

/** An `enabledIf` failure (FC disabled, fetch error, not registered yet, …) surfaces as `fallback: true`. */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
